import { useRef } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { ServerStatus } from "@/components/site/ServerStatus";
import { CommunitySection } from "@/components/site/CommunitySection";
import { LatestNews } from "@/components/site/LatestNews";
import { StoreTeaser } from "@/components/site/StoreTeaser";
import { Countdown } from "@/components/site/Countdown";
import { Gallery } from "@/components/site/Gallery";
import { PlayerHistory } from "@/components/site/PlayerHistory";
import { VideoBackdrop } from "@/components/site/VideoBackdrop";
import { StaffTeam } from "@/components/site/StaffTeam";
import { HeroParticles } from "@/components/site/HeroParticles";
import { CopyConnect } from "@/components/site/CopyConnect";
import { useText } from "@/lib/useText";
import { useServerStatus } from "@/context/ServerStatusContext";

function HeroLiveBadge() {
  const s = useServerStatus();
  if (s.online !== true) return null;
  return (
    <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-300 animate-fade-up" data-testid="hero-live-badge">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulseDot" aria-hidden="true" />
      {s.players} {s.players === 1 ? "pessoa" : "pessoas"} na cidade neste momento
    </p>
  );
}

export default function Home() {
  const { content } = useContent();
  const t = useText();
  const { hero, links, community, discord, media } = content;
  const sec = content.sections || {};
  const show = (k) => sec[k] !== false;
  const heroRef = useRef(null);
  const parRef = useRef(null);
  const onHeroMove = (e) => {
    const r = heroRef.current?.getBoundingClientRect();
    if (!r || !parRef.current) return;
    const x = ((e.clientX - r.left) / r.width - 0.5) * -18;
    const y = ((e.clientY - r.top) / r.height - 0.5) * -12;
    parRef.current.style.transform = `translate(${x}px, ${y}px) scale(1.04)`;
  };

  return (
    <>
      <section className="relative overflow-hidden border-b border-eden-border grain" aria-labelledby="hero-title" data-testid="hero-section" onMouseMove={onHeroMove} ref={heroRef}>
        <div ref={parRef} className="absolute inset-0 transition-transform duration-300 ease-out will-change-transform" style={{ transform: "scale(1.04)" }}>
        {media.hero_image_url ? (
          <img src={media.hero_image_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" aria-hidden="true" />
        ) : (
          <VideoBackdrop opacity={0.45} />
        )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-eden-bg via-eden-bg/70 to-eden-bg/30" aria-hidden="true" />
        {content.theme?.particles !== false && <HeroParticles />}
        <div className="container-eden relative grid gap-12 py-20 sm:py-28 lg:grid-cols-12 lg:items-end lg:py-36">
          <div className="lg:col-span-8">
            <p className="eyebrow animate-fade-up">{hero.eyebrow}</p>
            <HeroLiveBadge />
            <h1 id="hero-title" className="mt-6 max-w-3xl text-4xl font-extrabold leading-[1.05] tracking-tight text-eden-text animate-fade-up [animation-delay:80ms] sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {hero.title}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-eden-secondary animate-fade-up [animation-delay:160ms] sm:text-lg" data-testid="hero-description">{hero.description}</p>
            <div className="mt-10 flex flex-wrap items-center gap-4 animate-fade-up [animation-delay:240ms]">
              <a href={links.fivem_join} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="hero-play-button">
                {hero.cta_label} <ArrowUpRight size={18} aria-hidden="true" />
              </a>
              <p className="text-sm text-eden-muted">
                {t("connect_prefix")} <CopyConnect code={links.fivem_code} /> {t("connect_suffix")}
              </p>
            </div>
            {show("countdown") && <Countdown hero={hero} />}
          </div>
          <div className="lg:col-span-4 lg:flex lg:justify-end animate-fade-up [animation-delay:320ms]">
            <ServerStatus code={links.fivem_code} />
          </div>
        </div>
      </section>

      {show("player_history") && <PlayerHistory />}

      <section data-reveal className="py-20 sm:py-28" aria-labelledby="cidade-title" data-testid="city-section">
        <div className="container-eden grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-4">{t("city_eyebrow")}</p>
            <h2 id="cidade-title" className="text-2xl font-bold tracking-tight text-eden-text sm:text-3xl lg:text-4xl" data-testid="city-title">{hero.city_title}</h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <p className="text-base leading-relaxed text-eden-secondary sm:text-lg" data-testid="city-text">{hero.city_text}</p>
            {hero.city_points?.length > 0 && (
              <ul className="mt-8 divide-y divide-eden-border border-y border-eden-border">
                {hero.city_points.map((p, i) => (
                  <li key={p} className="flex items-start gap-4 py-4 text-sm text-eden-text sm:text-base" data-testid={`city-point-${i}`}>
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-eden-orange/60 text-eden-orange"><Check size={12} aria-hidden="true" /></span>
                    {p}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {show("gallery") && <Gallery />}
      <StaffTeam />
      {show("news") && <LatestNews />}
      {show("store") && <StoreTeaser />}
      {show("community") && <CommunitySection community={community} links={links} discord={discord} />}
    </>
  );
}
